import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { FiUser, FiLogOut, FiSettings, FiBarChart, FiRefreshCw, FiMessageCircle, FiMic, FiImage, FiTrendingUp, FiCalendar, FiArrowUp, FiCreditCard, FiDownload, FiInfo, FiArrowUpCircle, FiHome } from 'react-icons/fi';
import toast from "react-hot-toast";
import { FaPaw } from "react-icons/fa";
import UserSettingsSection from './UserSettingsSection';
import UpgradePrompt from '../ChatWidget/UpgradePrompt';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../ChatWidget/LoadingSpinner';
import { stripeAPI } from '../../api';

// Accent bar for header
const AccentBar = styled.div`
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #FF9900 0%, #FFD700 100%);
  border-radius: 0 0 12px 12px;
`;

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #232B3A 0%, #34495E 100%);
  padding: 2rem 1rem;
`;

const DashboardContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: #f8f9fb;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.10);
  overflow: hidden;
  border: 1px solid #f3f4f6;
`;

const Header = styled.div`
  background: #fff;
  padding: 2.2rem 2rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const UserAvatar = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FFB347 0%, #FFD700 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  font-weight: 700;
  border: 3px solid #fffbe6;
  box-shadow: 0 2px 12px rgba(255, 179, 71, 0.10);
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserName = styled.div`
  font-weight: 800;
  font-size: 1.45rem;
  color: #232B3A;
`;

const UserEmail = styled.div`
  color: #6b7280;
  font-size: 1rem;
  margin-top: 2px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.7rem;
`;

const IconActionButton = styled.button`
  background: none;
  color: #FF9900;
  border: 1.5px solid #FFEDD5;
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  font-size: 1.15rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-shadow: none;
  transition: all 0.16s;
  &:hover {
    background: #FFF7ED;
    color: #FF9900;
    border-color: #FFD700;
  }
`;

const MainContent = styled.div`
  padding: 2.5rem 2rem 2.5rem 2rem;
  background: transparent;
  color: #232B3A;
`;

const SectionTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 800;
  color: #FF9900;
  margin-bottom: 2.2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 2.5rem;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.07);
  border: 1.5px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7rem;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.color}10;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  border: 1px solid ${props => props.color}30;
`;

const StatTitle = styled.div`
  font-size: 1rem;
  color: #6b7280;
  font-weight: 700;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #232B3A;
  margin-bottom: 0.2rem;
`;

const StatSubtext = styled.div`
  font-size: 0.95rem;
  color: #6b7280;
`;

const PlanRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
`;

const PlanBadge = styled.span<{ plan: string }>`
  display: inline-block;
  padding: 0.4rem 1.1rem;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  background: ${({ plan }) =>
    plan === 'premium' ? '#FFD700' :
    plan === 'basic' ? '#FF9900' :
    '#6b7280'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const UpgradeButton = styled.button`
  position: relative;
  background: linear-gradient(135deg, #ffae00ff, #ffee00ff);

  color: #000000ff;
  border: none;
  border-radius: 999px;
  padding: 0.32rem 1.7rem 0.32rem 0.85rem;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(255, 153, 0, 0.10);
  cursor: pointer;
  margin-left: 0.7rem;
  margin-top: 0;
  min-width: 0;
  min-height: 0;
  transition: box-shadow 0.16s, transform 0.16s, border 0.16s;
  outline: none;
  &:hover, &:focus {
    box-shadow: 0 4px 16px rgba(255,153,0,0.18);
    transform: translateY(-1px) scale(1.03);
    border: 2px solid #FFD700;
  }
`;

const UpgradeButtonText = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.1px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UpgradeButtonIcon = styled.span`
  display: flex;
  align-items: center;
  font-size: 1em;
`;

const UpgradeBenefit = styled.div`
  font-size: 0.97rem;
  color: #FF9900;
  font-weight: 500;
  margin-top: 0.5rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2.5rem;
`;
const TabButton = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  color: ${({ active }) => (active ? '#FF9900' : '#6b7280')};
  font-size: 1.1rem;
  font-weight: 700;
  border-bottom: 3px solid ${({ active }) => (active ? '#FF9900' : 'transparent')};
  padding: 0.5rem 0;
  cursor: pointer;
  transition: color 0.18s, border-bottom 0.18s;
`;
const BillingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(44,62,80,0.06);
  margin-bottom: 2rem;
`;
const BillingTh = styled.th`
  background: #f3f4f6;
  color: #232B3A;
  font-weight: 700;
  padding: 1rem 0.7rem;
  text-align: left;
  border-bottom: 1.5px solid #e5e7eb;
`;
const BillingTd = styled.td`
  padding: 0.85rem 0.7rem;
  border-bottom: 1px solid #f3f4f6;
  color: #232B3A;
  font-size: 1rem;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.3em 0.9em;
  border-radius: 12px;
  font-size: 0.95em;
  font-weight: 700;
  color: #fff;
  background: ${({ status }) =>
    status === 'paid' ? '#22c55e' :
    status === 'open' ? '#f59e42' :
    status === 'void' ? '#dc2626' :
    '#6b7280'};
  text-transform: capitalize;
`;
const FilterRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
  background: #f8f9fa;
  padding: 1rem 1.2rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;

  label {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    font-weight: 600;
    color: #374151;
    font-size: 0.9rem;
  }
`;
const FilterSelect = styled.select`
  padding: 0.6rem 1rem;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  font-size: 0.95rem;
  color: #232B3A;
  background: #fff;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.7rem center;
  background-size: 1em;
  padding-right: 2.5rem;

  &:hover {
    border-color: #FF9900;
    box-shadow: 0 2px 8px rgba(255, 153, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #FF9900;
    box-shadow: 0 0 0 3px rgba(255, 153, 0, 0.1);
  }

  option {
    padding: 0.5rem;
    font-weight: 500;
  }
`;
const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  color: #FF9900;
  font-weight: 600;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1em;
  &:hover {
    text-decoration: underline;
    color: #d97706;
  }
`;
const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;
const EmptyState = styled.div`
  text-align: center;
  color: #6b7280;
  margin: 2.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
`;

const UpgradeCTACard = styled.div`
  background: linear-gradient(90deg, #fffbe6 0%, #fff 100%);
  border: 2px solid #FFEDD5;
  border-radius: 18px;
  padding: 1.5rem 1.2rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-shadow: 0 2px 8px rgba(255, 179, 71, 0.07);
  @media (max-width: 600px) { padding: 1.2rem 0.7rem; }
`;
const UpgradeCTATitle = styled.div`
  font-size: 1.2rem;
  font-weight: 800;
  color: #FF9900;
  margin-bottom: 0.5rem;
`;
const UpgradeCTABenefits = styled.ul`
  margin: 0 0 1rem 1.2rem;
  color: #6b7280;
  font-size: 1rem;
  list-style: disc;
`;
const UpgradeCTAButton = styled.button`
  background: linear-gradient(90deg, #FF9900 0%, #FFD700 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 0.7rem 1.7rem;
  font-size: 1.1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  box-shadow: 0 2px 8px rgba(255, 153, 0, 0.10);
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.18s;
  &:hover { background: linear-gradient(90deg, #FFD700 0%, #FF9900 100%); }
`;
const ProgressBar = styled.div`
  width: 100%;
  background: #f3f4f6;
  border-radius: 8px;
  height: 12px;
  margin: 0.5rem 0 1.2rem 0;
  position: relative;
`;
const ProgressFill = styled.div<{ percent: number; color: string }>`
  width: ${({ percent }) => percent}%;
  background: ${({ color }) => color};
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s;
`;
const ProgressLabel = styled.div`
  font-size: 0.97rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;
const PlanSummaryCard = styled.div`
  background: #fff;
  border: 1.5px solid #f3f4f6;
  border-radius: 14px;
  padding: 1.2rem 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(44,62,80,0.06);
`;

const TabPanelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  width: 100%;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 0;
  }
`;
const TabPanel = styled.div`
  flex: 1 1 0;
  min-width: 0;
  background: transparent;
`;
// Restore original TabsBar and TabButtonModern styles
const TabsBar = styled.div`
  display: flex;
  width: 100%;
  border-bottom: none;
  margin-bottom: 2.5rem;
  background: #f3f4f6;
  border-radius: 0;
  box-shadow: 0 2px 8px rgba(44,62,80,0.04);
`;
const TabButtonModern = styled.button<{ active: boolean }>`
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ active }) => (active ? '#fff' : '#f3f4f6')};
  color: ${({ active }) => (active ? '#232B3A' : '#6b7280')};
  font-size: 1.1rem;
  font-weight: ${({ active }) => (active ? 800 : 700)};
  border: none;
  border-bottom: 3px solid ${({ active }) => (active ? '#FF9900' : 'transparent')};
  border-radius: 0;
  padding: 0.7rem 0;
  cursor: pointer;
  transition: color 0.18s, background 0.18s, border-bottom 0.18s, box-shadow 0.18s;
  outline: none;
  box-shadow: ${({ active }) => (active ? '0 2px 8px rgba(44,62,80,0.06)' : 'none')};
  z-index: ${({ active }) => (active ? 2 : 1)};
  & svg {
    margin-right: 4px;
    font-size: 1.2em;
  }
`;

const UserDashboard: React.FC = () => {
  const { user, usage, limits, signOut, refreshUser, loading } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const navigate = useNavigate();
  const [imageMessageCount, setImageMessageCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'usage' | 'billing'>('usage');
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'status'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
const [showConsultationModal, setShowConsultationModal] = useState(false);

const HandleAlreadySub = () => {
  if (user && (user.plan_type === "basic" || user.plan_type === "premium")) {
    // ✅ Open the booking link for subscribers
    window.open(
      "https://api.leadconnectorhq.com/widget/bookings/first-training-wodt",
      "_blank"
    );
  } else {
    // ⚠️ Show toast message if user is not subscribed
    toast.error("Please upgrade your plan first to book a consultation!");
  }
};

  // Get unique plans and statuses for filters
  const planOptions = Array.from(new Set(billingHistory.map(b => b.plan))).filter(Boolean);
  const statusOptions = Array.from(new Set(billingHistory.map(b => b.status))).filter(Boolean);

  // Filter and sort billing history
  const filteredHistory = billingHistory
    .filter(b => (planFilter === 'all' || b.plan === planFilter) && (statusFilter === 'all' || b.status === statusFilter))
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'date') cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (sortBy === 'amount') cmp = a.amount - b.amount;
      if (sortBy === 'status') cmp = a.status.localeCompare(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  useEffect(() => {
    async function fetchImageStats() {
      try {
        const res = await fetch('/api/history/stats', { credentials: 'include' });
        const data = await res.json();
        if (data.success && data.stats && data.stats.byType && typeof data.stats.byType.image === 'number') {
          setImageMessageCount(data.stats.byType.image);
        }
      } catch (e) {
        // handle error silently
      }
    }
    if (user) fetchImageStats();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'billing') {
      setBillingLoading(true);
      setBillingError(null);
      stripeAPI.getBillingHistory()
        .then(data => {
          console.log('Billing history response:', data);
          if (data.success) {
            setBillingHistory(data.history || []);
          } else {
            setBillingError('Failed to load billing history.');
          }
        })
        .catch((error) => {
          console.error('Billing history error:', error);
          setBillingError('Failed to load billing history.');
        })
        .finally(() => setBillingLoading(false));
    }
  }, [activeTab]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshUser();
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };


const handleUpgrade = async (plan: 'free' | 'free-trial' | 'basic' | 'premium') => {
  try {
    setUpgradeLoading(true);

    const response = await stripeAPI.createCheckoutSession(plan);

    if (!response.success) {
      console.error('Failed to create checkout session:', response);
      alert('❌ Failed to start upgrade process. Please try again.');
      return;
    }

    if (response.url) {
      // Redirect to Stripe Checkout (works for basic, premium, and free-trial)
      window.location.href = response.url;
    } else {
      console.error('Missing checkout URL:', response);
      alert('❌ Something went wrong. Please try again.');
    }
  } catch (error) {
    console.error('Upgrade error:', error);
    alert('❌ Failed to start upgrade process. Please try again.');
  } finally {
    setUpgradeLoading(false);
  }
};



  const handleCloseSettings = async () => {
    setShowSettings(false);
    await refreshUser();
  };

  if (loading) {
    return (
      <DashboardContainer>
        <DashboardContent style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <LoadingSpinner size="large" text="Loading your dashboard..." />
        </DashboardContent>
      </DashboardContainer>
    );
  }

  if (!user) {
    return (
      <DashboardContainer>
        <DashboardContent>
          <div style={{ padding: '4rem 2rem', textAlign: 'center', color: '#6b7280' }}>
            <FiUser style={{ fontSize: 40, marginBottom: 16 }} />
            <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>Please log in to view your dashboard</div>
            <div style={{ marginBottom: 16 }}>

            </div>
            <button
              style={{ background: '#6b7280', color: '#fff', border: 'none', padding: '0.75rem 1.5rem', borderRadius: 12, fontSize: 16, cursor: 'pointer' }}
              onClick={() => window.location.href = '/'}
            >
              ← Back to Home
            </button>
          </div>
        </DashboardContent>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
    <IconActionButton
    onClick={() => navigate('/')}
    title="Back to Home"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: '#FF9900',
      color: '#fff',
      border: 'none',
      padding: '0.6rem 1.3rem',
      borderRadius: '10px',
      fontWeight: 600,
      fontSize: '0.95rem',
      width: '150px', // ⬅️ Increase width here
      justifyContent: 'center',
      transition: 'all 0.2s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = '#FFD700';
      e.currentTarget.style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = '#FF9900';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    <FiHome size={18} />
    Back to Home
  </IconActionButton>

      <DashboardContent>
        <AccentBar />
        <Header>
          <HeaderRow>
            <HeaderMain>
              <UserAvatar>
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <FaPaw size={38} />
                )}
              </UserAvatar>
              <UserDetails>
                <UserName>Welcome, {user.name || 'User'}!</UserName>
                <UserEmail>{user.email}</UserEmail>
              </UserDetails>
            </HeaderMain>
<HeaderActions>
  {/* 🏠 Go to Home */}
  <IconActionButton onClick={() => navigate('/')} title="Back to Home">
    <FiHome />
  </IconActionButton>

  {/* ⚙️ Settings */}
  <IconActionButton onClick={() => setShowSettings(true)} title="Settings">
    <FiSettings />
  </IconActionButton>

  {/* 🚪 Logout */}
  <IconActionButton onClick={handleLogout} title="Logout">
    <FiLogOut />
  </IconActionButton>
</HeaderActions>

          </HeaderRow>
        </Header>
        {showSettings ? (
          <UserSettingsSection onBack={handleCloseSettings} />
        ) : (
          <>
            <TabsBar>
              <TabButtonModern active={activeTab === 'usage'} onClick={() => setActiveTab('usage')}><FiBarChart />Usage</TabButtonModern>
              <TabButtonModern active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}><FiCreditCard />Billing</TabButtonModern>
            </TabsBar>
            {activeTab === 'usage' && (
          <MainContent>
            <SectionTitle>Your Usage Overview</SectionTitle>
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatTitle>Current Plan                   <PlanBadge plan={user.plan_type}>
                    {user.plan_type.charAt(0).toUpperCase() + user.plan_type.slice(1)}
                  </PlanBadge></StatTitle>
                  <StatIcon color="#FF9900">
                    <FiBarChart />
                  </StatIcon>
                </StatHeader>
                <PlanRow>

<div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
  {/* Upgrade Button – Always Visible */}
  <UpgradeButton onClick={() => setShowUpgrade(true)}>
    <UpgradeButtonIcon><FiArrowUp /></UpgradeButtonIcon>
    <UpgradeButtonText>
      {user.plan_type === 'premium' ? 'Upgrade Plan' : 'Upgrade to Premium'}
    </UpgradeButtonText>
  </UpgradeButton>

  {/* Book Consultation Button */}
  <UpgradeButton
    style={{
      background: 'linear-gradient(90deg, #818181ff 0%, #696969ff 100%)',
      color: '#fff',
      border: 'none',
    }}
    onClick={() => setShowConsultationModal(true)}
  >
    <UpgradeButtonIcon><FiCalendar /></UpgradeButtonIcon>
    <UpgradeButtonText className=''>Book a Consultation</UpgradeButtonText>
  </UpgradeButton>
</div>

                </PlanRow>
                {user.plan_type === 'free' && (
                  <UpgradeBenefit>Unlock unlimited messages, voice, and more with Premium!</UpgradeBenefit>
                )}
                {user.plan_type === 'basic' && (
                  <UpgradeBenefit>Upgrade to Premium for unlimited everything!</UpgradeBenefit>
                )}
                <StatSubtext>Your subscription plan</StatSubtext>
              </StatCard>
              <StatCard>
                <StatHeader>
                      <StatTitle>Messages Sent <FiInfo title="Total messages sent / plan limit" style={{marginLeft:4}}/></StatTitle>
                  <StatIcon color="#FFB300">
                    <FiMessageCircle />
                  </StatIcon>
                </StatHeader>
                    <ProgressLabel>
                      {usage?.text_messages_sent || 0} / {limits?.text_messages === -1 ? '∞' : limits?.text_messages}
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={
                        !limits || typeof limits.text_messages !== 'number' ? 0 :
                        limits.text_messages === -1 ? 100 : Math.min(100, Math.round(((usage?.text_messages_sent || 0) / (typeof limits.text_messages === 'number' && limits.text_messages !== 0 ? limits.text_messages : 1)) * 100))
                      } color="#FFB300" />
                    </ProgressBar>
                    <StatSubtext>Text messages used</StatSubtext>
              </StatCard>
              <StatCard>
                <StatHeader>
                      <StatTitle>Voice Messages <FiInfo title="Total voice messages used / plan limit" style={{marginLeft:4}}/></StatTitle>
                      <StatIcon color="#6366f1">
                    <FiMic />
                  </StatIcon>
                </StatHeader>
                    <ProgressLabel>
                      {Math.round(usage?.voice_minutes_used || 0)} / {limits?.voice_minutes === -1 ? '∞' : limits?.voice_minutes}
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={
                        !limits || typeof limits.voice_minutes !== 'number' ? 0 :
                        limits.voice_minutes === -1 ? 100 : Math.min(100, Math.round(((usage?.voice_minutes_used || 0) / (typeof limits.voice_minutes === 'number' && limits.voice_minutes !== 0 ? limits.voice_minutes : 1)) * 100))
                      } color="#6366f1" />
                    </ProgressBar>
                    <StatSubtext>Voice messages used</StatSubtext>
              </StatCard>
              <StatCard>
                <StatHeader>
                      <StatTitle>Image Uploads <FiInfo title="Total images uploaded / plan limit" style={{marginLeft:4}}/></StatTitle>
                      <StatIcon color="#FF9900">
                    <FiImage />
                  </StatIcon>
                </StatHeader>
                    <ProgressLabel>
                      {usage?.image_uploads || 0} / {limits?.image_uploads === -1 ? '∞' : limits?.image_uploads}
                    </ProgressLabel>
                    <ProgressBar>
                      <ProgressFill percent={
                        !limits || typeof limits.image_uploads !== 'number' ? 0 :
                        limits.image_uploads === -1 ? 100 : Math.min(100, Math.round(((usage?.image_uploads || 0) / (typeof limits.image_uploads === 'number' && limits.image_uploads !== 0 ? limits.image_uploads : 1)) * 100))
                      } color="#FF9900" />
                    </ProgressBar>
                    <StatSubtext>Images uploaded</StatSubtext>
              </StatCard>
            </StatsGrid>
              </MainContent>
            )}
            {activeTab === 'billing' && (
              <MainContent>
                <SectionTitle>Billing History</SectionTitle>
                {filteredHistory.length > 0 && (
                  <FilterRow>
                    <label>
                      Plan:
                      <FilterSelect value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
                        <option value="all">All</option>
                        {planOptions.map(plan => <option key={plan} value={plan}>{plan}</option>)}
                      </FilterSelect>
                    </label>
                    <label>
                      Status:
                      <FilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="all">All</option>
                        {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                      </FilterSelect>
                    </label>
                  </FilterRow>
                )}
                {billingLoading ? (
                  <LoadingSpinner size="medium" text="Loading billing history..." />
                ) : billingError ? (
                  <div style={{color:'#dc2626',margin:'1.5rem 0'}}>{billingError}</div>
                ) : (
                  <ResponsiveTableWrapper>
                    <BillingTable>
                      <thead>
                        <tr>
                          <BillingTh style={{cursor:'pointer'}} onClick={() => {setSortBy('date');setSortDir(sortBy==='date'&&sortDir==='desc'?'asc':'desc')}}>
                            Date {sortBy==='date' ? (sortDir==='asc'?'▲':'▼') : ''}
                          </BillingTh>
                          <BillingTh style={{cursor:'pointer'}} onClick={() => {setSortBy('amount');setSortDir(sortBy==='amount'&&sortDir==='desc'?'asc':'desc')}}>
                            Amount {sortBy==='amount' ? (sortDir==='asc'?'▲':'▼') : ''}
                          </BillingTh>
                          <BillingTh>Plan</BillingTh>
                          <BillingTh style={{cursor:'pointer'}} onClick={() => {setSortBy('status');setSortDir(sortBy==='status'&&sortDir==='desc'?'asc':'desc')}}>
                            Status {sortBy==='status' ? (sortDir==='asc'?'▲':'▼') : ''}
                          </BillingTh>
                          <BillingTh>Payment Method</BillingTh>
                          <BillingTh>Invoice</BillingTh>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHistory.length === 0 ? (
                          <tr>
                            <BillingTd colSpan={6} style={{textAlign:'center', color:'#6b7280', fontStyle:'italic', fontSize:'1.05em'}}>
                              No purchase history found.
                            </BillingTd>
                          </tr>
                        ) : filteredHistory.map((item) => (
                          <tr key={item.id}>
                            <BillingTd>{new Date(item.date).toLocaleDateString()}</BillingTd>
                            <BillingTd>{item.amount.toLocaleString(undefined, { style: 'currency', currency: item.currency?.toUpperCase() || 'USD' })}</BillingTd>
                            <BillingTd>{item.plan}</BillingTd>
                            <BillingTd><StatusBadge status={item.status}>{item.status}</StatusBadge></BillingTd>
                            <BillingTd>
                              {item.paymentMethod ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <FiCreditCard style={{ color: '#6b7280' }} />
                                  <span>
                                    {item.paymentMethod.brand?.toUpperCase() || 'CARD'} •••• {item.paymentMethod.last4 || '****'}
                                  </span>
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not available</span>
                              )}
                            </BillingTd>
                            <BillingTd>
                              {item.invoicePdf ? (
                                <DownloadButton href={item.invoicePdf} target="_blank" rel="noopener noreferrer" download>
                                  <FiDownload /> PDF
                                </DownloadButton>
                              ) : item.hostedInvoiceUrl ? (
                                <DownloadButton href={item.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                                  <FiDownload /> View
                                </DownloadButton>
                              ) : (
                                'N/A'
                              )}
                            </BillingTd>
                          </tr>
                        ))}
                      </tbody>
                    </BillingTable>
                  </ResponsiveTableWrapper>
                )}
          </MainContent>
        )}


{showUpgrade && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] overflow-y-auto">
    <div className="relative bg-white rounded-2xl p-8 shadow-2xl max-w-4xl w-full my-10">

      {/* ❌ Close (cross) button at top-right */}
      <button
        onClick={() => setShowUpgrade(false)}
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition transform hover:scale-110"
        title="Close"
      >
        ✕
      </button>

      <h2 className="text-2xl font-bold text-primary mb-8 text-center">
        Choose Your Plan
      </h2>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Basic Plan */}
        <div className="border rounded-2xl p-6 shadow hover:shadow-xl transition flex flex-col justify-between">
          <div>
            <h4 className="text-xl font-bold text-primary mb-2">Basic</h4>
            <p className="text-2xl font-bold text-orange mb-1">$9.99</p>
            <p className="text-sm text-primary mb-4">per month</p>
            <ul className="text-sm text-primary space-y-2 mb-4">
              <li>✔ 1000 text messages</li>
              <li>✔ 20 voice minutes</li>
              <li>✔ 10 image uploads</li>
              <li>✔ Voice Notes + Image Analysis</li>
            </ul>
          </div>
          <button
            onClick={() => handleUpgrade('basic')}
            className="w-full py-2 bg-orange text-white rounded-lg font-semibold hover:bg-orange/90 transition"
          >
            Start Basic Plan
          </button>
        </div>

        {/* Premium Plan */}
        <div className="border-2 border-orange bg-orange/10 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition flex flex-col justify-between relative">
          <span className="absolute top-0 right-0 bg-orange text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            Most Popular
          </span>
          <div>
            <h4 className="text-xl font-bold text-primary mb-2">Premium</h4>
            <p className="text-2xl font-bold text-orange mb-1">$19.99</p>
            <p className="text-sm text-primary mb-4">per month</p>
            <ul className="text-sm text-primary space-y-2 mb-4">
              <li>✔ 2500 text messages</li>
              <li>✔ 50 voice minutes</li>
              <li>✔ 15 image uploads</li>
              <li>✔ Everything in Basic plan</li>
              <li>✔ Pre-recorded video guidance</li>
            </ul>
          </div>
          <button
            onClick={() => handleUpgrade('premium')}
            className="w-full py-2 bg-orange text-white rounded-lg font-semibold hover:bg-orange/90 transition"
          >
            Start Premium Plan
          </button>
        </div>

        {/* Ultimate Plan */}
        <div className="border rounded-2xl p-6 shadow hover:shadow-xl transition opacity-70 flex flex-col justify-between relative">
          <span className="absolute top-0 right-0 bg-gray-400 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            Coming Soon
          </span>
          <div>
            <h4 className="text-xl font-bold text-primary mb-2">Ultimate</h4>
            <p className="text-2xl font-bold text-orange mb-1">$29.99</p>
            <p className="text-sm text-primary mb-4">per month</p>
            <ul className="text-sm text-primary space-y-2 mb-4">
              <li>✔ 4000 text messages</li>
              <li>✔ 75 voice minutes</li>
              <li>✔ 25 image uploads</li>
              <li>✔ Everything in Premium plan</li>
              <li>✔ Video upload analysis</li>
            </ul>
          </div>
          <button
            disabled
            className="w-full py-2 bg-gray-400 text-white rounded-lg font-semibold cursor-not-allowed"
          >
            Coming Soon
          </button>
        </div>
      </div>
    </div>
  </div>
)}


{showConsultationModal && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] overflow-y-auto">
    <div className="relative bg-white rounded-2xl p-8 shadow-2xl max-w-4xl w-full my-10">

      {/* ❌ Close (cross) button at top-right */}
      <button
        onClick={() => setShowConsultationModal(false)}
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition transform hover:scale-110"
        title="Close"
      >
        ✕
      </button>

      <h2 className="text-2xl font-bold text-primary mb-6 text-center">
        Choose an Option
      </h2>

      <div className="space-y-4 mb-10">
        {/* Subscriber Option */}
        <button
          onClick={() => HandleAlreadySub()}
          className="w-full py-3 bg-orange text-white rounded-lg font-semibold hover:bg-orange/90 transition flex items-center justify-between px-4"
        >
          <span>Already a Subscriber</span>
          <span className="text-sm font-normal">$99.99</span>
        </button>

        {/* Guest Option */}
        <button
          onClick={() =>
            window.open(
              "https://api.leadconnectorhq.com/widget/bookings/first-training-wodttjybiq",
              "_blank"
            )
          }
          className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition flex items-center justify-between px-4"
        >
          <span>Continue Without Subscription</span>
          <span className="text-sm font-normal">$149.99</span>
        </button>
      </div>

      {/* Become a Subscriber Section */}
      <h3 className="text-2xl font-bold text-primary mb-8 text-center">
        Become a Subscriber
      </h3>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Basic Plan */}
        <div className="border rounded-2xl p-6 shadow hover:shadow-xl transition flex flex-col justify-between">
          <div>
            <h4 className="text-xl font-bold text-primary mb-2">Basic</h4>
            <p className="text-2xl font-bold text-orange mb-1">$9.99</p>
            <p className="text-sm text-primary mb-4">per month</p>
            <ul className="text-sm text-primary space-y-2 mb-4">
              <li>✔ 1000 text messages</li>
              <li>✔ 20 voice minutes</li>
              <li>✔ 10 image uploads</li>
              <li>✔ Voice Notes + Image Analysis</li>
            </ul>
          </div>
          <button
            onClick={() => handleUpgrade('basic')}
            className="w-full py-2 bg-orange text-white rounded-lg font-semibold hover:bg-orange/90 transition"
          >
            Start Basic Plan
          </button>
        </div>

        {/* Premium Plan */}
        <div className="border-2 border-orange bg-orange/10 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition flex flex-col justify-between relative">
          <span className="absolute top-0 right-0 bg-orange text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            Most Popular
          </span>
          <div>
            <h4 className="text-xl font-bold text-primary mb-2">Premium</h4>
            <p className="text-2xl font-bold text-orange mb-1">$19.99</p>
            <p className="text-sm text-primary mb-4">per month</p>
            <ul className="text-sm text-primary space-y-2 mb-4">
              <li>✔ 2500 text messages</li>
              <li>✔ 50 voice minutes</li>
              <li>✔ 15 image uploads</li>
              <li>✔ Everything in Basic plan</li>
              <li>✔ Pre-recorded video guidance</li>
            </ul>
          </div>
          <button
            onClick={() => handleUpgrade('premium')}
            className="w-full py-2 bg-orange text-white rounded-lg font-semibold hover:bg-orange/90 transition"
          >
            Start Premium Plan
          </button>
        </div>

        {/* Ultimate Plan */}
        <div className="border rounded-2xl p-6 shadow hover:shadow-xl transition opacity-70 flex flex-col justify-between relative">
          <span className="absolute top-0 right-0 bg-gray-400 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            Coming Soon
          </span>
          <div>
            <h4 className="text-xl font-bold text-primary mb-2">Ultimate</h4>
            <p className="text-2xl font-bold text-orange mb-1">$29.99</p>
            <p className="text-sm text-primary mb-4">per month</p>
            <ul className="text-sm text-primary space-y-2 mb-4">
              <li>✔ 4000 text messages</li>
              <li>✔ 75 voice minutes</li>
              <li>✔ 25 image uploads</li>
              <li>✔ Everything in Premium plan</li>
              <li>✔ Video upload analysis</li>
            </ul>
          </div>
          <button
            disabled
            className="w-full py-2 bg-gray-400 text-white rounded-lg font-semibold cursor-not-allowed"
          >
            Coming Soon
          </button>
        </div>
      </div>
    </div>
  </div>
)}


          </>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};

export default UserDashboard;